import * as React from 'react';

import { useQuery } from '@apollo/client';
import {
  ExplorerIndexQuery,
  ProductLookupByNamesQuery,
  type ExplorerIndexRow,
  type ProductLookupRow,
} from '@graphql/queries/PerceptionModelQueries';
import { Box, Button, Typography } from '@mui/material';

import { explorerPoints } from './explorerPoints';
import FlavorSpaceMap from './FlavorSpaceMap';
import { distanceToAnchors, type PeerBearing } from './normSetUtils';
import PerceptionModelError from './PerceptionModelError';
import SelectedProductPanel from './SelectedProductPanel';
import TokenFilter from './TokenFilter';
import useMapView from './useMapView';
import useTokenSearch from './useTokenSearch';

/**
 * Explore the perception data, and pick from it.
 *
 * Replaces the token-list custom candidates panel: the same token search is
 * still here, but it highlights points on the map instead of producing a list,
 * so a product is chosen from where it sits rather than from its name.
 *
 * Read-only without `onToggle` — the standalone explorer has no norm set to add
 * to.
 */

type Props = {
  jobId?: number;
  anchorRows?: PeerBearing[];
  /**
   * The anchors by name, when the caller has no peer lists to hand.
   *
   * A saved norm set records its anchors as names. The peer lists behind them
   * exist to score a product being added, so a read-only view can colour its
   * anchors without fetching them — `anchorRows` stays the input for the
   * builder, which does need to measure.
   */
  anchorNames?: Set<string>;
  anchorMean?: Record<string, number>;
  attributes?: string[];
  included?: Set<string>;
  /**
   * Token search — the "Highlight by taste" field.
   *
   * A way of finding a product you have not named yet, so it belongs where
   * something is being chosen: the two pickers, and the explorer, whose whole
   * job is search. A saved norm set has already made that choice, and lighting
   * up products it does not contain only invites a change it will not accept.
   */
  highlightByTaste?: boolean;
  /**
   * Open fitted to the anchors and members rather than to the whole universe.
   *
   * True where the screen is about a set that already exists — a norm set is a
   * fraction of a percent of the space, so showing all sixteen thousand
   * products draws it as a few pixels. False where the point is to find
   * something the set does not contain yet.
   */
  focusOnSet?: boolean;
  /** what the product is being added to, for the button label */
  targetLabel?: string;
  onToggle?: (product: string) => void;
};

const EMPTY_SET: Set<string> = new Set();

/**
 * Products the export gave no coordinates, named rather than dropped.
 *
 * A product with no position is not on the map, and saying nothing would leave
 * it looking like it simply is not in the model.
 */
const UnplacedNote: React.FC<{ unplaced: string[] }> = ({ unplaced }) => {
  if (unplaced.length === 0) return null;

  const one = unplaced.length === 1;

  return (
    <Typography variant="caption" sx={{ color: 'warning.main' }}>
      {unplaced.length} product{one ? '' : 's'} could not be placed, so{' '}
      {one ? 'it is' : 'they are'} missing from the map:{' '}
      {unplaced.slice(0, 5).join(', ')}
      {unplaced.length > 5 && '…'}
    </Typography>
  );
};

/**
 * Whether a product can be added.
 *
 * Placeability is a question about the anchors: can this product be measured
 * against them. With no anchors supplied there is nothing to measure against,
 * so the question does not apply — answering "no" would disable the button for
 * every product rather than for an unplaceable one.
 */
const isPlaceable = (anchorRows: PeerBearing[], product: string): boolean =>
  !anchorRows.length || !!distanceToAnchors(anchorRows, product);

const PerceptionDataExplorer: React.FC<Props> = ({
  jobId,
  anchorRows = [],
  anchorNames,
  anchorMean = {},
  attributes = [],
  included = EMPTY_SET,
  highlightByTaste = true,
  focusOnSet = false,
  targetLabel,
  onToggle,
}) => {
  const [selected, setSelected] = React.useState<string | undefined>();

  // The map: name and coordinates for every product, ~1.1 MB at 16k products.
  const { data, loading, error } = useQuery(ExplorerIndexQuery, {
    variables: { jobId },
    skip: !jobId,
  });

  const rows: ExplorerIndexRow[] =
    data?.allPerceptionModelProductLookups?.nodes ?? [];

  const search = useTokenSearch(jobId, highlightByTaste);
  const matched = search.matched;

  const anchors = React.useMemo(
    () => anchorNames ?? new Set(anchorRows.map((r) => r.productName)),
    [anchorNames, anchorRows],
  );

  const { points, unplaced } = React.useMemo(
    () => explorerPoints(rows, { anchors, included, matched }),
    [rows, anchors, included, matched],
  );

  /**
   * What window of the space to draw, and whether it is fitted to the set.
   *
   * Fitted to the norm set, its members occupy a fraction of a percent of the
   * whole space — correctly drawn and still unreadable. Fitted to itself, it is
   * the picture the page is asking for. The whole space is one click away,
   * because finding something *not* in the set is the other real question.
   */
  const view = useMapView(points, focusOnSet);

  // The clicked product only. Keeps the index light rather than pulling ggvars
  // for every product up front; Apollo serves repeat clicks from cache.
  const { data: rowData, loading: rowLoading } = useQuery(
    ProductLookupByNamesQuery,
    {
      variables: { jobId, names: selected ? [selected] : [] },
      skip: !jobId || !selected,
    },
  );
  const detail: ProductLookupRow | undefined =
    rowData?.allPerceptionModelProductLookups?.nodes?.[0];

  if (error) return <PerceptionModelError error={error} />;

  return (
    <Box>
      {highlightByTaste && (
        <TokenFilter
          vocabulary={search.vocabulary}
          value={search.tokens}
          onChange={search.setTokens}
          matchCount={search.matched.size}
          loading={search.loading}
          unavailable={search.unavailable}
        />
      )}

      {loading && <Typography variant="body2">Loading the space…</Typography>}

      {view.canFocus && (
        <Button
          size="small"
          onClick={() => view.setWholeSpace(!view.wholeSpace)}
          sx={{ textTransform: 'none', mb: 0.5 }}
        >
          {view.wholeSpace
            ? `Fit to these ${view.marked.length} products`
            : 'Show the whole space'}
        </Button>
      )}

      <FlavorSpaceMap
        points={view.visible}
        onSelect={setSelected}
        selected={selected}
        box={view.box}
        offscreen={view.offscreen}
      />

      <UnplacedNote unplaced={unplaced} />

      {selected && (
        <SelectedProductPanel
          product={selected}
          row={detail}
          rowLoading={rowLoading}
          anchorMean={anchorMean}
          // With no anchors there is nothing to compare against, so the profile
          // is read on its own terms rather than against an empty reference.
          attributes={
            attributes.length
              ? attributes
              : Object.keys(detail?.ggvars ?? {}).sort()
          }
          isAnchor={anchors.has(selected)}
          isIncluded={included.has(selected)}
          placeable={isPlaceable(anchorRows, selected)}
          targetLabel={targetLabel}
          onToggle={onToggle}
        />
      )}
    </Box>
  );
};

export default PerceptionDataExplorer;
